import type {
	IErrorHandler,
	ErrorContext,
	ErrorReport,
	ErrorRecoveryStrategy
} from '../interfaces/IErrorHandler';
import { nanoid } from 'nanoid';

export class ErrorBoundaryService implements IErrorHandler {
	private static instance: ErrorBoundaryService;
	private errorReports: ErrorReport[] = [];
	private recoveryStrategies: ErrorRecoveryStrategy[] = [];
	private errorListeners: Set<(report: ErrorReport) => void> = new Set();
	private maxErrorHistory = 100;

	private constructor() {
		// Set up global error handlers
		if (typeof window !== 'undefined') {
			window.addEventListener('error', this.handleWindowError);
			window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
		}
	}

	static getInstance(): ErrorBoundaryService {
		if (!ErrorBoundaryService.instance) {
			ErrorBoundaryService.instance = new ErrorBoundaryService();
		}
		return ErrorBoundaryService.instance;
	}

	async handle(error: Error, context?: Partial<ErrorContext>): Promise<void> {
		const report = this.createErrorReport(error, context);
		this.addErrorReport(report);

		// Log to console in development
		if (import.meta.env.DEV) {
			console.error('Error caught:', {
				error: error.message,
				stack: error.stack,
				context: report.context
			});
		}

		// Notify listeners
		this.notifyListeners(report);

		// Send to remote logging in production
		if (import.meta.env.PROD) {
			await this.sendToRemoteLogging(report);
		}
	}

	async handleWithRecovery(error: Error, context?: Partial<ErrorContext>): Promise<boolean> {
		const report = this.createErrorReport(error, context);

		// Try recovery strategies
		for (const strategy of this.recoveryStrategies) {
			if (strategy.canRecover(error)) {
				try {
					await strategy.recover(error, report.context);
					report.handled = true;
					this.addErrorReport(report);
					return true;
				} catch (recoveryError) {
					// Recovery failed, continue to next strategy
					console.error('Recovery strategy failed:', recoveryError);
				}
			}
		}

		// No recovery possible, handle normally
		await this.handle(error, context);
		return false;
	}

	registerRecoveryStrategy(strategy: ErrorRecoveryStrategy): void {
		this.recoveryStrategies.push(strategy);
	}

	getRecentErrors(limit = 10): ErrorReport[] {
		return this.errorReports.slice(-limit);
	}

	clearErrors(): void {
		this.errorReports = [];
	}

	onError(callback: (report: ErrorReport) => void): () => void {
		this.errorListeners.add(callback);
		return () => this.errorListeners.delete(callback);
	}

	private createErrorReport(error: Error, context?: Partial<ErrorContext>): ErrorReport {
		return {
			id: nanoid(),
			error,
			context: {
				timestamp: new Date(),
				...context
			},
			handled: false,
			userNotified: false
		};
	}

	private addErrorReport(report: ErrorReport): void {
		this.errorReports.push(report);

		// Maintain max history
		if (this.errorReports.length > this.maxErrorHistory) {
			this.errorReports = this.errorReports.slice(-this.maxErrorHistory);
		}
	}

	private notifyListeners(report: ErrorReport): void {
		this.errorListeners.forEach((listener) => {
			try {
				listener(report);
			} catch (error) {
				console.error('Error in error listener:', error);
			}
		});
	}

	private async sendToRemoteLogging(report: ErrorReport): Promise<void> {
		// TODO: Implement remote logging
		// This would send to your logging service (e.g., Sentry, LogRocket, etc.)
	}

	private handleWindowError = (event: ErrorEvent): void => {
		const error = new Error(event.message);
		error.stack = event.error?.stack || `${event.filename}:${event.lineno}:${event.colno}`;

		this.handle(error, {
			service: 'window',
			method: 'error',
			additional: {
				filename: event.filename,
				lineno: event.lineno,
				colno: event.colno
			}
		});
	};

	private handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
		const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));

		this.handle(error, {
			service: 'window',
			method: 'unhandledRejection',
			additional: {
				reason: event.reason,
				promise: event.promise
			}
		});
	};

	destroy(): void {
		if (typeof window !== 'undefined') {
			window.removeEventListener('error', this.handleWindowError);
			window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
		}
		this.errorListeners.clear();
		this.errorReports = [];
		this.recoveryStrategies = [];
	}
}

// Export singleton instance
export const errorBoundary = ErrorBoundaryService.getInstance();

// Common recovery strategies
export class NetworkErrorRecoveryStrategy implements ErrorRecoveryStrategy {
	private retryCount = new Map<string, number>();
	private maxRetries = 3;

	canRecover(error: Error): boolean {
		return error.name === 'NetworkError' || error.message.includes('fetch');
	}

	async recover(error: Error, context: ErrorContext): Promise<void> {
		const key = `${context.service}-${context.method}`;
		const currentRetries = this.retryCount.get(key) || 0;

		if (currentRetries >= this.maxRetries) {
			this.retryCount.delete(key);
			throw new Error('Max retries exceeded');
		}

		this.retryCount.set(key, currentRetries + 1);

		// Wait before retry with exponential backoff
		const delay = Math.min(1000 * Math.pow(2, currentRetries), 10000);
		await new Promise((resolve) => setTimeout(resolve, delay));

		// Recovery logic would go here (e.g., retry the request)
		// This is a placeholder - actual implementation would retry the operation
	}
}

export class LocalStorageErrorRecoveryStrategy implements ErrorRecoveryStrategy {
	canRecover(error: Error): boolean {
		return (
			error.name === 'QuotaExceededError' ||
			(error.message.includes('localStorage') && error.message.includes('quota'))
		);
	}

	async recover(error: Error, context: ErrorContext): Promise<void> {
		try {
			// Try to clear old data
			const keys = Object.keys(localStorage);
			const oldestKeys = keys
				.filter((key) => key.includes('timestamp'))
				.sort()
				.slice(0, Math.floor(keys.length * 0.2)); // Remove oldest 20%

			oldestKeys.forEach((key) => localStorage.removeItem(key));

			// If still failing, clear all non-essential data
			if (localStorage.length > 0.8 * 5242880) {
				// 80% of 5MB limit
				keys.forEach((key) => {
					if (!key.includes('auth') && !key.includes('user')) {
						localStorage.removeItem(key);
					}
				});
			}
		} catch (clearError) {
			throw new Error('Failed to recover from localStorage quota error');
		}
	}
}
